import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { TriangleAlert } from 'lucide-react';
import type { DuplicateCandidate } from '@product-portal/shared';
import { RequestStatusPill } from '@/components/portal/status-pill';
import { formatDayMonth } from '@/lib/format';

/** Words of the text that the search found in common, highlighted (« mots en commun surlignés »). */
export function Highlight({ text, words }: { text: string; words: readonly string[] }) {
  if (words.length === 0) return <>{text}</>;
  const wanted = new Set(words.map((w) => w.toLocaleLowerCase('fr')));
  // Split on word boundaries, keeping the separators (« d'article » → « d' », « article »).
  const parts = text.split(/([\p{L}\p{N}-]+)/u);
  return (
    <>
      {parts.map((part, i) =>
        wanted.has(part.toLocaleLowerCase('fr')) ? (
          <mark key={i} className="rounded-sm bg-warning-border px-0.5 text-inherit">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

/** « 2 équipes la suivent déjà » (team of the request and teams of its subscribers). */
export function teamsFollowingLabel(count: number): string | null {
  return count >= 2 ? `${count} équipes la suivent déjà` : null;
}

/** « PP-305 · Rédaction Pharma », or « Autre équipe » for a request the user cannot read. */
export function candidateOrigin(d: DuplicateCandidate): string {
  return `${d.reference} · ${d.readable && d.teamName ? d.teamName : 'Autre équipe'}`;
}

/**
 * Side panel of 02 and 15 (ADR-009): the closest requests, delivered ones
 * included. For a request of another team the API only sends reference,
 * title and status — nothing is hidden here, there is nothing more to hide.
 */
export function DuplicatePanel({
  items,
  scopeLabel,
  intro,
  action,
}: {
  items: DuplicateCandidate[];
  /** « du CMS Europa » or « de tous les produits ». */
  scopeLabel: string;
  intro: string;
  action: (d: DuplicateCandidate) => ReactNode;
}) {
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-3 rounded-card border border-warning-border bg-warning px-5 py-4" aria-live="polite">
      <div className="flex items-center gap-2">
        <TriangleAlert aria-hidden="true" className="size-[18px] text-warning-foreground" />
        <h2 className="m-0 text-[15px] font-bold text-warning-foreground">{items.length === 1 ? '1 demande proche' : `${items.length} demandes proches`}</h2>
      </div>
      <p className="m-0 text-[13px] text-foreground-secondary">
        Recherche sur toutes les demandes {scopeLabel}, livrées comprises, à partir de votre titre et de votre description. {intro}
      </p>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {items.map((d) => (
          <li key={d.id} className="flex flex-col gap-2 rounded-control border border-border bg-card px-3.5 py-3">
            <span className="text-sm font-bold">
              <Highlight text={d.title} words={d.titleMatches} />
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{candidateOrigin(d)}</span>
              <RequestStatusPill status={d.status} />
            </div>
            {teamsFollowingLabel(d.teamsConcerned) && <span className="text-xs font-bold text-foreground-secondary">{teamsFollowingLabel(d.teamsConcerned)}</span>}
            {!d.readable && <span className="text-xs text-muted-foreground">Demande d'une autre équipe : seuls la référence, le titre et le statut sont visibles</span>}
            {d.status === 'delivered' && (
              <>
                <span className="text-xs text-muted-foreground">
                  {d.deliveredAt ? `Livrée le ${formatDayMonth(d.deliveredAt)}` : 'Livrée'} : votre problème est peut-être déjà corrigé, ou c'est une régression à
                  signaler
                </span>
                {d.releaseNote && (
                  <Link to={`/release-notes?note=${d.releaseNote.id}`} target="_blank" className="text-[13px] font-bold no-underline">
                    Voir la release note {d.releaseNote.versionLabel} →
                  </Link>
                )}
              </>
            )}
            {action(d)}
          </li>
        ))}
      </ul>
    </section>
  );
}
